/**
 * TrainingCatalogCsvImportDialog
 * 
 * Self-contained CSV import dialog for training catalog admin.
 * Parses CSV, previews rows, validates, and imports artifacts + offerings + capability maps.
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Upload,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  parseTrainingCsv,
  importTrainingCsv,
  generateTemplateCsv,
  generateErrorCsv,
  type TrainingCsvRow,
  type ImportSummary,
  type ImportOptions,
  type ImportError,
} from '@/utils/trainingCsvImport';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
}

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
];

export function TrainingCatalogCsvImportDialog({ open, onOpenChange, onImported }: Props) {
  const queryClient = useQueryClient();

  // Parse state
  const [parsedRows, setParsedRows] = useState<TrainingCsvRow[]>([]);
  const [parseErrors, setParseErrors] = useState<ImportError[]>([]);
  const [fileName, setFileName] = useState('');

  // Options
  const [defaultProvider, setDefaultProvider] = useState('VISION Hi-Tech Training');
  const [defaultTimezone, setDefaultTimezone] = useState('America/Chicago');
  const [defaultCity, setDefaultCity] = useState('Overland Park');
  const [defaultState, setDefaultState] = useState('KS');
  const [defaultVenue, setDefaultVenue] = useState('Overland Park Convention Center');
  const [dryRun, setDryRun] = useState(true);

  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportSummary | null>(null);

  const resetState = useCallback(() => {
    setParsedRows([]);
    setParseErrors([]);
    setFileName('');
    setImportResult(null);
    setIsImporting(false);
  }, []);

  const handleOpenChange = useCallback(
    (val: boolean) => {
      if (!val) resetState();
      onOpenChange(val);
    },
    [onOpenChange, resetState]
  );

  // ── File selection ────────────────────────────────────────────────
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportResult(null);
    setFileName(file.name);

    const { rows, errors } = await parseTrainingCsv(file);
    setParsedRows(rows);
    setParseErrors(errors);

    if (errors.length > 0 && rows.length === 0) {
      toast.error(`CSV parsing failed with ${errors.length} error(s)`);
    } else if (errors.length > 0) {
      toast.warning(`Parsed ${rows.length} rows with ${errors.length} warning(s)`);
    } else {
      toast.success(`Parsed ${rows.length} rows successfully`);
    }
  }, []);

  // ── Template download ─────────────────────────────────────────────
  const handleDownloadTemplate = useCallback(() => {
    const csv = generateTemplateCsv();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'training_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // ── Import ────────────────────────────────────────────────────────
  const handleImport = useCallback(async () => {
    if (parsedRows.length === 0) return;
    setIsImporting(true);
    setImportResult(null);

    const options: ImportOptions = {
      defaultProviderName: defaultProvider,
      defaultTimezone,
      defaultCity,
      defaultState,
      defaultVenueName: defaultVenue,
      dryRun,
    };

    try {
      const result = await importTrainingCsv(parsedRows, options);
      setImportResult(result);

      if (dryRun) {
        toast.info('Dry run complete — no data was written');
      } else if (result.errors.length === 0) {
        toast.success(
          `Import complete: ${result.created_artifacts} artifacts, ${result.created_offerings} offerings, ${result.created_capability_maps} capability maps`
        );
        queryClient.invalidateQueries({ queryKey: ['recommended-training'] });
        queryClient.invalidateQueries({ queryKey: ['offering-instances'] });
        queryClient.invalidateQueries({ queryKey: ['artifact-capability-map-counts'] });
        onImported?.();
      } else {
        toast.warning(
          `Import completed with ${result.errors.length} error(s). ${result.created_artifacts} artifacts, ${result.created_offerings} offerings created.`
        );
        queryClient.invalidateQueries({ queryKey: ['recommended-training'] });
        queryClient.invalidateQueries({ queryKey: ['offering-instances'] });
        queryClient.invalidateQueries({ queryKey: ['artifact-capability-map-counts'] });
        onImported?.();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      toast.error(msg);
    } finally {
      setIsImporting(false);
    }
  }, [parsedRows, defaultProvider, defaultTimezone, defaultCity, defaultState, defaultVenue, dryRun, queryClient, onImported]);

  // ── Error CSV download ────────────────────────────────────────────
  const handleDownloadErrors = useCallback(() => {
    const allErrors = [...parseErrors, ...(importResult?.errors || [])];
    if (allErrors.length === 0) return;
    const csv = generateErrorCsv(allErrors);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import_errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [parseErrors, importResult]);

  const previewRows = parsedRows.slice(0, 10);
  const allErrors = [...parseErrors, ...(importResult?.errors || [])];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Training CSV
          </DialogTitle>
          <DialogDescription>
            Import training artifacts and class schedules from a CSV file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* File Input + Template */}
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="csv-file">CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mt-1"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Template
            </Button>
          </div>

          {fileName && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              {fileName} — {parsedRows.length} valid row{parsedRows.length !== 1 ? 's' : ''}
              {parseErrors.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {parseErrors.length} error{parseErrors.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          )}

          {/* Preview Table */}
          {previewRows.length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">
                  Preview (first {previewRows.length} of {parsedRows.length} rows)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Title</TableHead>
                        <TableHead className="text-xs">Start Date</TableHead>
                        <TableHead className="text-xs">Time</TableHead>
                        <TableHead className="text-xs">Instructor</TableHead>
                        <TableHead className="text-xs">Code</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewRows.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-medium max-w-[200px] truncate">
                            {row.title}
                          </TableCell>
                          <TableCell className="text-xs">{row.start_date}</TableCell>
                          <TableCell className="text-xs">
                            {row.start_time}–{row.end_time}
                          </TableCell>
                          <TableCell className="text-xs">{row.instructor_name || '—'}</TableCell>
                          <TableCell className="text-xs">{row.session_code || '—'}</TableCell>
                          <TableCell className="text-xs">{row.delivery_type || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Import Options */}
          {parsedRows.length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Import Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Default Provider</Label>
                    <Input
                      value={defaultProvider}
                      onChange={(e) => setDefaultProvider(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Default Timezone</Label>
                    <Select value={defaultTimezone} onValueChange={setDefaultTimezone}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_TIMEZONES.map((tz) => (
                          <SelectItem key={tz} value={tz}>
                            {tz}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Default City</Label>
                    <Input
                      value={defaultCity}
                      onChange={(e) => setDefaultCity(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Default State</Label>
                    <Input
                      value={defaultState}
                      onChange={(e) => setDefaultState(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Default Venue</Label>
                    <Input
                      value={defaultVenue}
                      onChange={(e) => setDefaultVenue(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                  <div>
                    <Label className="text-sm font-medium">Dry Run</Label>
                    <p className="text-xs text-muted-foreground">
                      Preview import results without writing to database
                    </p>
                  </div>
                  <Switch checked={dryRun} onCheckedChange={setDryRun} />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Import Results */}
          {importResult && (
            <Card className={dryRun ? 'border-primary/30 bg-primary/5' : 'border-accent/30 bg-accent/5'}>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  {dryRun ? (
                    <AlertCircle className="h-4 w-4 text-primary" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-accent-foreground" />
                  )}
                  {dryRun ? 'Dry Run Results (estimated)' : 'Import Results'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Artifacts Created</p>
                    <p className="font-semibold">{importResult.created_artifacts}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Artifacts Skipped (deduped)</p>
                    <p className="font-semibold">{importResult.skipped_artifacts}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Offerings Created</p>
                    <p className="font-semibold">{importResult.created_offerings}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Offerings Skipped (deduped)</p>
                    <p className="font-semibold">{importResult.skipped_offerings}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Capability Maps</p>
                    <p className="font-semibold">{importResult.created_capability_maps}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Rows Skipped</p>
                    <p className="font-semibold">{importResult.skipped_rows}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Errors */}
          {allErrors.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-destructive">
                  {allErrors.length} error{allErrors.length !== 1 ? 's' : ''}
                </p>
                <Button variant="outline" size="sm" onClick={handleDownloadErrors}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Errors CSV
                </Button>
              </div>
              <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                {allErrors.slice(0, 10).map((e, i) => (
                  <p key={i} className="text-destructive">
                    Row {e.row}: {e.reason}
                  </p>
                ))}
                {allErrors.length > 10 && (
                  <p className="text-muted-foreground">
                    ... and {allErrors.length - 10} more (download CSV for full list)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Close
          </Button>
          <Button
            onClick={handleImport}
            disabled={parsedRows.length === 0 || isImporting}
          >
            {isImporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {dryRun ? 'Running dry run...' : 'Importing...'}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {dryRun ? 'Dry Run' : `Import ${parsedRows.length} Rows`}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
